import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Pencil, Upload, RefreshCw, Trash, Download } from "lucide-react";
import { toast } from "sonner";
import { resolveImageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = ["Food", "Santé", "Sport", "Bien-être", "Service", "Famille"];
const STATUSES = ["draft", "pending", "active", "inactive", "expired"];

const emptyPartner = {
  name: "", category: "Food", description: "", long_description: "",
  offer: "", offer_value: 0, monthly_estimated_uses: 1, monthly_cap: 15,
  address: "", city: "Berchem-Sainte-Agathe", website: "", instagram: "",
  facebook: "", phone: "", email: "", image_url: "", conditions: "",
  start_date: "", end_date: "", status: "active",
  featured: false, verified: true, counts_in_counter: true, spotlight: false, priority: 0,
  badges: [], admin_notes: "",
};

export default function AdminPartners() {
  const { user: me } = useAuth();
  const canEdit = me?.role === "super_admin" || me?.role === "marketing";
  const canDelete = me?.role === "super_admin";
  const canExport = me?.role === "super_admin" || me?.role === "marketing";
  const canReset = me?.role === "super_admin";
  const [partners, setPartners] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [editOpen, setEditOpen] = useState(false);
  const [editPartner, setEditPartner] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/partners" + (statusFilter !== "all" ? `?status=${statusFilter}` : ""));
    setPartners(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const openNew = () => { setEditPartner({ ...emptyPartner }); setEditOpen(true); };
  const openEdit = (p) => { setEditPartner({ ...p, badges: p.badges || [] }); setEditOpen(true); };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...editPartner, badges: editPartner.badges || [] };
      if (editPartner.id) {
        await api.patch(`/admin/partners/${editPartner.id}`, payload);
      } else {
        await api.post("/admin/partners", payload);
      }
      toast.success("Partenaire enregistré");
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error("Erreur à l'enregistrement");
    } finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!confirm(`Supprimer définitivement "${p.name}" ?`)) return;
    await api.delete(`/admin/partners/${p.id}`);
    toast.success("Partenaire supprimé");
    load();
  };

  const resetDb = async () => {
    if (!confirm("Vider complètement la base (partenaires + soumissions) ? Cette action est irréversible.")) return;
    await api.post("/admin/reset");
    toast.success("Base vidée");
    load();
  };

  const reseed = async () => {
    await api.post("/admin/seed");
    toast.success("Partenaires démo rechargés");
    load();
  };

  const exportCsv = async () => {
    try {
      const res = await fetch(`${API}/admin/export/partners.csv`, { credentials: "include" });
      if (!res.ok) throw new Error("Erreur");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bhealth-partners-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé");
    } catch {
      toast.error("Export impossible");
    }
  };

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setEditPartner((p) => ({ ...p, image_url: data.url }));
    toast.success("Image uploadée");
  };

  return (
    <div className="space-y-6" data-testid="admin-partners-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-navy-900/60 font-semibold">Partenaires</div>
          <h1 className="font-heading font-extrabold text-4xl text-navy-900 tracking-tighter mt-1">Gestion du réseau</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {canExport && <button onClick={exportCsv} className="btn-ghost text-sm" data-testid="admin-export-csv-btn"><Download className="w-4 h-4" /> Export CSV</button>}
          {canReset && <button onClick={reseed} className="btn-ghost text-sm" data-testid="admin-reseed-btn"><RefreshCw className="w-4 h-4" /> Re-seeder démo</button>}
          {canReset && <button onClick={resetDb} className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full border border-destructive/30 text-destructive hover:bg-destructive/5" data-testid="admin-reset-btn"><Trash className="w-4 h-4" /> Vider la base</button>}
          {canEdit && <button onClick={openNew} className="btn-lime text-sm" data-testid="admin-new-partner-btn"><Plus className="w-4 h-4" /> Ajouter</button>}
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setStatusFilter("all")} className={`chip px-4 py-2 border ${statusFilter === "all" ? "bg-navy-900 text-white border-navy-900" : "bg-white border-navy-900/10"}`}>Tous</button>
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)} className={`chip px-4 py-2 border ${statusFilter === s ? "bg-navy-900 text-white border-navy-900" : "bg-white border-navy-900/10"}`}>{s}</button>
        ))}
      </div>

      <div className="card-premium overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr className="text-left">
              <th className="p-4 text-xs uppercase tracking-wider text-navy-900/70">Partenaire</th>
              <th className="p-4 text-xs uppercase tracking-wider text-navy-900/70">Catégorie</th>
              <th className="p-4 text-xs uppercase tracking-wider text-navy-900/70">Offre</th>
              <th className="p-4 text-xs uppercase tracking-wider text-navy-900/70">Valeur/mois</th>
              <th className="p-4 text-xs uppercase tracking-wider text-navy-900/70" title="Ouvertures de fiche · Clics 'Je profite'">Vues · Intent</th>
              <th className="p-4 text-xs uppercase tracking-wider text-navy-900/70">Statut</th>
              <th className="p-4 text-xs uppercase tracking-wider text-navy-900/70">Actions</th>
            </tr>
          </thead>
          <tbody>
            {partners.map((p) => (
              <tr key={p.id} className="border-t border-navy-900/5 hover:bg-muted/50">
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    {p.image_url && <img src={resolveImageUrl(p.image_url)} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                    <div>
                      <div className="font-semibold text-navy-900">{p.name}</div>
                      <div className="text-xs text-muted-foreground">{p.city}</div>
                    </div>
                  </div>
                </td>
                <td className="p-4">{p.category}</td>
                <td className="p-4 max-w-xs truncate">{p.offer}</td>
                <td className="p-4">{Math.min((p.offer_value || 0) * (p.monthly_estimated_uses || 1), p.monthly_cap || 15).toFixed(0)}€</td>
                <td className="p-4 text-sm tabular-nums">
                  <span className="text-navy-900/70">👁 {p.views_count || 0}</span>
                  <span className="mx-2 text-navy-900/20">·</span>
                  <span className="font-semibold text-lime-700">🎯 {p.redeems_count || 0}</span>
                </td>
                <td className="p-4">
                  <span className={`chip ${p.status === "active" ? "bg-lime-500/20 text-navy-900" : p.status === "pending" ? "bg-amber-100 text-amber-900" : "bg-muted text-navy-900/70"}`}>{p.status}</span>
                </td>
                <td className="p-4">
                  <div className="flex gap-1">
                    {canEdit && <button onClick={() => openEdit(p)} data-testid={`edit-partner-${p.id}`} className="p-2 rounded-lg hover:bg-navy-900/5"><Pencil className="w-4 h-4" /></button>}
                    {canDelete && <button onClick={() => remove(p)} data-testid={`delete-partner-${p.id}`} className="p-2 rounded-lg hover:bg-destructive/10 text-destructive"><Trash2 className="w-4 h-4" /></button>}
                    {!canEdit && !canDelete && <span className="text-xs text-muted-foreground">—</span>}
                  </div>
                </td>
              </tr>
            ))}
            {!partners.length && (
              <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">Aucun partenaire.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editPartner?.id ? "Modifier" : "Ajouter"} un partenaire</DialogTitle></DialogHeader>
          {editPartner && (
            <div className="space-y-4 mt-2">
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Nom"><Input value={editPartner.name} onChange={(e) => setEditPartner({ ...editPartner, name: e.target.value })} data-testid="edit-name" /></Field>
                <Field label="Catégorie">
                  <Select value={editPartner.category} onValueChange={(v) => setEditPartner({ ...editPartner, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Offre" className="md:col-span-2"><Input value={editPartner.offer} onChange={(e) => setEditPartner({ ...editPartner, offer: e.target.value })} data-testid="edit-offer" /></Field>
                <Field label="Description courte" className="md:col-span-2"><Input value={editPartner.description} onChange={(e) => setEditPartner({ ...editPartner, description: e.target.value })} /></Field>
                <Field label="Description longue" className="md:col-span-2"><Textarea rows={3} value={editPartner.long_description || ""} onChange={(e) => setEditPartner({ ...editPartner, long_description: e.target.value })} /></Field>
                <Field label="Adresse"><Input value={editPartner.address} onChange={(e) => setEditPartner({ ...editPartner, address: e.target.value })} /></Field>
                <Field label="Ville"><Input value={editPartner.city} onChange={(e) => setEditPartner({ ...editPartner, city: e.target.value })} /></Field>
                <Field label="Téléphone"><Input value={editPartner.phone || ""} onChange={(e) => setEditPartner({ ...editPartner, phone: e.target.value })} /></Field>
                <Field label="Email"><Input value={editPartner.email || ""} onChange={(e) => setEditPartner({ ...editPartner, email: e.target.value })} /></Field>
                <Field label="Site web"><Input value={editPartner.website || ""} onChange={(e) => setEditPartner({ ...editPartner, website: e.target.value })} /></Field>
                <Field label="Instagram"><Input value={editPartner.instagram || ""} onChange={(e) => setEditPartner({ ...editPartner, instagram: e.target.value })} /></Field>

                <Field label="Valeur unitaire (€)"><Input type="number" step="0.5" value={editPartner.offer_value} onChange={(e) => setEditPartner({ ...editPartner, offer_value: parseFloat(e.target.value) || 0 })} /></Field>
                <Field label="Usages/mois estimés"><Input type="number" step="0.1" value={editPartner.monthly_estimated_uses} onChange={(e) => setEditPartner({ ...editPartner, monthly_estimated_uses: parseFloat(e.target.value) || 0 })} /></Field>
                <Field label="Plafond mensuel (€)"><Input type="number" step="1" value={editPartner.monthly_cap} onChange={(e) => setEditPartner({ ...editPartner, monthly_cap: parseFloat(e.target.value) || 0 })} /></Field>
                <Field label="Priorité (0–10)"><Input type="number" value={editPartner.priority || 0} onChange={(e) => setEditPartner({ ...editPartner, priority: parseInt(e.target.value) || 0 })} /></Field>
                <Field label="Date début"><Input type="date" value={editPartner.start_date || ""} onChange={(e) => setEditPartner({ ...editPartner, start_date: e.target.value })} /></Field>
                <Field label="Date fin"><Input type="date" value={editPartner.end_date || ""} onChange={(e) => setEditPartner({ ...editPartner, end_date: e.target.value })} /></Field>
                <Field label="Badges (virgule)" className="md:col-span-2"><Input value={(editPartner.badges || []).join(", ")} onChange={(e) => setEditPartner({ ...editPartner, badges: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} /></Field>
                <Field label="Statut">
                  <Select value={editPartner.status} onValueChange={(v) => setEditPartner({ ...editPartner, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Image URL"><Input value={editPartner.image_url || ""} onChange={(e) => setEditPartner({ ...editPartner, image_url: e.target.value })} /></Field>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold mb-1.5 block">Upload image</Label>
                <label className="btn-ghost text-sm cursor-pointer inline-flex"><Upload className="w-4 h-4" /> Choisir un fichier
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])} data-testid="edit-upload-input" />
                </label>
                {editPartner.image_url && <img src={resolveImageUrl(editPartner.image_url)} alt="" className="mt-3 rounded-xl w-40 h-28 object-cover" />}
              </div>

              <div className="flex flex-wrap gap-6 pt-2">
                <ToggleField label="Coup de cœur" checked={editPartner.featured} onChange={(v) => setEditPartner({ ...editPartner, featured: v })} />
                <ToggleField label="Vérifié" checked={editPartner.verified} onChange={(v) => setEditPartner({ ...editPartner, verified: v })} />
                <ToggleField label="Alimente le compteur" checked={editPartner.counts_in_counter} onChange={(v) => setEditPartner({ ...editPartner, counts_in_counter: v })} />
                <ToggleField label="Partenaire du mois (pin)" checked={!!editPartner.spotlight} onChange={(v) => setEditPartner({ ...editPartner, spotlight: v })} />
              </div>

              <Field label="Notes internes"><Textarea rows={2} value={editPartner.admin_notes || ""} onChange={(e) => setEditPartner({ ...editPartner, admin_notes: e.target.value })} /></Field>

              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setEditOpen(false)} className="btn-ghost text-sm">Annuler</button>
                <button onClick={save} disabled={busy} className="btn-navy text-sm" data-testid="edit-save-btn">{busy ? "Enregistrement…" : "Enregistrer"}</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs uppercase tracking-wider font-semibold mb-1.5 block text-navy-900/70">{label}</Label>
      {children}
    </div>
  );
}

function ToggleField({ label, checked, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <Switch checked={checked} onCheckedChange={onChange} />
      <span className="text-sm text-navy-900 font-medium">{label}</span>
    </div>
  );
}
